import Excel from "exceljs";
import FileSaver from "file-saver";

const TITLE = "DANH SÁCH ĐĂNG KÝ CƠM TRƯA";
const HEADER_FILL = "FB985B";
const TOTAL_FILL = "FFF1E8";
const BORDER_COLOR = "D9DEE7";
const DAILY_BORDER_COLOR = "7F8C9F";

function normalizeMonth(month) {
  const value = String(month || "");
  if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(value)) {
    throw new Error("Tháng xuất Excel không hợp lệ.");
  }
  return value;
}

function normalizeDate(date) {
  const value = String(date || "");
  if (!/^\d{4}-(0[1-9]|1[0-2])-([0-2]\d|3[01])$/.test(value)) {
    throw new Error("Ngày xuất Excel không hợp lệ.");
  }
  const parsed = toExcelDate(value);
  if (
    Number.isNaN(parsed.getTime()) ||
    `${parsed.getUTCFullYear()}-${String(parsed.getUTCMonth() + 1).padStart(
      2,
      "0"
    )}-${String(parsed.getUTCDate()).padStart(2, "0")}` !== value
  ) {
    throw new Error("Ngày xuất Excel không hợp lệ.");
  }
  return value;
}

function getMonthLabel(month) {
  const [year, monthNumber] = normalizeMonth(month).split("-");
  return `${monthNumber}/${year}`;
}

function getExportDepartmentName(options = {}) {
  return options.departmentName || "Tất cả phòng ban";
}

function getFileNamePart(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/g, "d")
    .replace(/Đ/g, "D")
    .replace(/[^a-zA-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function toExcelDate(dateKey) {
  const [year, month, day] = String(dateKey || "").split("-").map(Number);
  // ExcelJS serializes Date values from UTC. Creating the value at local
  // midnight makes dates in UTC+7 appear as the previous day in Excel.
  return new Date(Date.UTC(year, month - 1, day));
}

function getItemKey(item) {
  return item.foodId || `name:${item.foodName || "Chưa xác định"}`;
}

function getQuantity(item) {
  const quantity = Number(item && item.quantity);
  return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
}

function getUnitPrice(item) {
  const price = Number(item && item.price);
  return Number.isFinite(price) ? price : 0;
}

function getAmount(item) {
  const amount = Number(item && item.total);
  return Number.isFinite(amount) ? amount : getUnitPrice(item) * getQuantity(item);
}

export function buildLunchExportData(registrations = [], departmentOptions = [], month) {
  const normalizedMonth = normalizeMonth(month);
  const monthRegistrations = registrations
    .filter((registration) =>
      registration &&
      String(registration.date || "").startsWith(`${normalizedMonth}-`) &&
      (!registration.status || registration.status === "REGISTERED")
    )
    .slice()
    .sort((a, b) =>
      `${a.date}-${a.employeeNo || ""}-${a.employeeName || ""}`.localeCompare(
        `${b.date}-${b.employeeNo || ""}-${b.employeeName || ""}`
      )
    );

  const departmentMap = new Map();
  departmentOptions.forEach((department) => {
    if (department && department.id) {
      departmentMap.set(department.id, {
        id: department.id,
        name: department.name || department.code || "Chưa xác định"
      });
    }
  });
  monthRegistrations.forEach((registration) => {
    const id = registration.departmentId || "unknown";
    if (!departmentMap.has(id)) {
      departmentMap.set(id, {
        id,
        name: registration.departmentName || "Chưa xác định"
      });
    }
  });
  const activeDepartmentIds = new Set(
    monthRegistrations.map(registration => registration.departmentId || "unknown")
  );
  const departments = Array.from(departmentMap.values())
    .filter(department => activeDepartmentIds.has(department.id))
    .sort((a, b) => a.name.localeCompare(b.name, "vi"));

  const foodMap = new Map();
  monthRegistrations.forEach((registration) => {
    (registration.items || []).forEach((item) => {
      const key = getItemKey(item);
      if (!foodMap.has(key)) {
        foodMap.set(key, { key, name: item.foodName || "Chưa xác định" });
      }
    });
  });
  const foods = Array.from(foodMap.values()).sort((a, b) => a.name.localeCompare(b.name, "vi"));
  const dates = [...new Set(monthRegistrations.map((registration) => registration.date))].sort();

  const getRegistrationQuantity = registration =>
    (registration.items || []).reduce(
      (sum, item) => sum + getQuantity(item),
      0
    );
  const getRegistrationAmount = registration =>
    (registration.items || []).reduce((sum, item) => sum + getAmount(item), 0);

  const dailyRows = dates.map(date => {
    const dateRegistrations = monthRegistrations.filter(
      registration => registration.date === date
    );
    return {
      date,
      registrationCount: dateRegistrations.length,
      totalQuantity: dateRegistrations.reduce(
        (sum, registration) => sum + getRegistrationQuantity(registration),
        0
      ),
      totalAmount: dateRegistrations.reduce(
        (sum, registration) => sum + getRegistrationAmount(registration),
        0
      )
    };
  });

  const departmentRows = departments.map(department => {
    const departmentRegistrations = monthRegistrations.filter(
      registration =>
        (registration.departmentId || "unknown") === department.id
    );
    return {
      name: department.name,
      registrationCount: departmentRegistrations.length,
      totalQuantity: departmentRegistrations.reduce(
        (sum, registration) => sum + getRegistrationQuantity(registration),
        0
      ),
      totalAmount: departmentRegistrations.reduce(
        (sum, registration) => sum + getRegistrationAmount(registration),
        0
      )
    };
  });

  const foodRows = foods.map(food => {
    const matchingItems = monthRegistrations.reduce((result, registration) => {
      return [
        ...result,
        ...(registration.items || []).filter(item => getItemKey(item) === food.key)
      ];
    }, []);
    return {
      name: food.name,
      totalQuantity: matchingItems.reduce(
        (sum, item) => sum + getQuantity(item),
        0
      ),
      totalAmount: matchingItems.reduce(
        (sum, item) => sum + getAmount(item),
        0
      )
    };
  });

  const totals = dailyRows.reduce(
    (result, row) => ({
      registrationCount: result.registrationCount + row.registrationCount,
      totalQuantity: result.totalQuantity + row.totalQuantity,
      totalAmount: result.totalAmount + row.totalAmount
    }),
    { registrationCount: 0, totalQuantity: 0, totalAmount: 0 }
  );

  return {
    month: normalizedMonth,
    departments,
    foods,
    dailyRows,
    departmentRows,
    foodRows,
    totals,
    activeDayCount: dates.length,
    registrationCount: monthRegistrations.length
  };
}

function applyCellBorder(cell) {
  cell.border = {
    top: { style: "thin", color: { argb: BORDER_COLOR } },
    left: { style: "thin", color: { argb: BORDER_COLOR } },
    bottom: { style: "thin", color: { argb: BORDER_COLOR } },
    right: { style: "thin", color: { argb: BORDER_COLOR } }
  };
}

function prepareWorksheet(worksheet, month, columnCount, options = {}) {
  worksheet.mergeCells(1, 1, 1, columnCount);
  worksheet.getCell(1, 1).value = TITLE;
  worksheet.getCell(1, 1).font = { bold: true, size: 16 };
  worksheet.getCell(1, 1).alignment = { horizontal: "center", vertical: "middle" };
  worksheet.getRow(1).height = 26;
  worksheet.mergeCells(2, 1, 2, columnCount);
  worksheet.getCell(2, 1).value = `Tháng: ${getMonthLabel(month)}`;
  worksheet.getCell(2, 1).font = { bold: true, size: 11 };
  worksheet.getCell(2, 1).alignment = { horizontal: "center", vertical: "middle" };
  worksheet.mergeCells(3, 1, 3, columnCount);
  worksheet.getCell(3, 1).value = `Phòng ban: ${getExportDepartmentName(options)}`;
  worksheet.getCell(3, 1).font = { italic: true, size: 11 };
  worksheet.getCell(3, 1).alignment = { horizontal: "center", vertical: "middle" };
  worksheet.views = [{ state: "frozen", ySplit: 4 }];
}

function styleHeader(row) {
  row.height = 34;
  row.eachCell((cell) => {
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: HEADER_FILL } };
    cell.font = { bold: true, color: { argb: "FFFFFF" } };
    cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
    applyCellBorder(cell);
  });
}

function styleTableRows(worksheet, firstRow, lastRow, totalRow, leftAlignedColumns = []) {
  for (let rowNumber = firstRow; rowNumber <= lastRow; rowNumber += 1) {
    const row = worksheet.getRow(rowNumber);
    row.eachCell({ includeEmpty: true }, (cell, columnNumber) => {
      cell.alignment = {
        horizontal: leftAlignedColumns.includes(columnNumber) ? "left" : "center",
        vertical: "middle",
        wrapText: true
      };
      applyCellBorder(cell);
    });
  }
  if (totalRow) {
    totalRow.eachCell({ includeEmpty: true }, (cell) => {
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: TOTAL_FILL } };
      cell.font = { bold: true };
      cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
      applyCellBorder(cell);
    });
  }
}

function applyMinimumFontSize(worksheet, minimumSize = 14) {
  worksheet.eachRow(row => {
    row.eachCell({ includeEmpty: true }, cell => {
      const currentSize = Number(cell.font && cell.font.size) || 0;
      cell.font = {
        ...(cell.font || {}),
        size: Math.max(currentSize, minimumSize)
      };
    });
  });
}

function styleSectionTitle(worksheet, rowNumber, title, columnCount = 4) {
  worksheet.mergeCells(rowNumber, 1, rowNumber, columnCount);
  const cell = worksheet.getCell(rowNumber, 1);
  cell.value = title;
  cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: TOTAL_FILL } };
  cell.font = { bold: true, size: 12 };
  cell.alignment = { horizontal: "left", vertical: "middle" };
  worksheet.getRow(rowNumber).height = 24;
}

function styleMetricRows(worksheet, rowNumbers) {
  rowNumbers.forEach(rowNumber => {
    worksheet.getRow(rowNumber).eachCell({ includeEmpty: true }, cell => {
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: TOTAL_FILL } };
      cell.alignment = { horizontal: "center", vertical: "middle" };
      applyCellBorder(cell);
    });
    worksheet.getCell(rowNumber, 1).font = { bold: true };
    worksheet.getCell(rowNumber, 3).font = { bold: true };
  });
}

function addOverviewWorksheet(workbook, data, options) {
  const worksheet = workbook.addWorksheet("Tổng quan tháng");
  worksheet.columns = [
    { key: "date", width: 26 },
    { key: "registrationCount", width: 22 },
    { key: "totalQuantity", width: 25 },
    { key: "totalAmount", width: 20 }
  ];
  prepareWorksheet(worksheet, data.month, 4, options);

  worksheet.getRow(4).values = [
    "Tổng lượt đăng ký",
    data.totals.registrationCount,
    "Tổng số lượng món",
    data.totals.totalQuantity
  ];
  worksheet.getRow(5).values = [
    "Tổng chi phí",
    data.totals.totalAmount,
    "Số ngày có đăng ký",
    data.activeDayCount
  ];
  worksheet.getCell("B5").numFmt = "#,##0 [$₫-vi-VN]";
  styleMetricRows(worksheet, [4, 5]);

  const headerRow = worksheet.getRow(8);
  headerRow.values = [
    "Ngày",
    "Số người đăng ký",
    "Tổng số lượng món",
    "Tổng tiền"
  ];
  styleHeader(headerRow);

  data.dailyRows.forEach(sourceRow => {
    const row = worksheet.addRow([
      toExcelDate(sourceRow.date),
      sourceRow.registrationCount,
      sourceRow.totalQuantity,
      sourceRow.totalAmount
    ]);
    row.getCell(1).numFmt = "dd/mm/yyyy";
    row.getCell(4).numFmt = "#,##0 [$₫-vi-VN]";
  });
  const totalRow = worksheet.addRow([
    "TỔNG THÁNG",
    data.totals.registrationCount,
    data.totals.totalQuantity,
    data.totals.totalAmount
  ]);
  totalRow.getCell(4).numFmt = "#,##0 [$₫-vi-VN]";
  styleTableRows(worksheet, 9, totalRow.number - 1, totalRow);
  worksheet.autoFilter = {
    from: { row: 8, column: 1 },
    to: { row: 8, column: 4 }
  };
  worksheet.views = [{ state: "frozen", ySplit: 8 }];
  return worksheet;
}

function addDepartmentWorksheet(workbook, data, options) {
  const worksheet = workbook.addWorksheet("Theo phòng ban");
  worksheet.columns = [
    { width: 10 },
    { width: 32 },
    { width: 20 },
    { width: 20 },
    { width: 20 }
  ];
  prepareWorksheet(worksheet, data.month, 5, options);

  styleSectionTitle(worksheet, 4, "THỐNG KÊ THEO PHÒNG BAN", 5);
  const departmentHeader = worksheet.getRow(5);
  departmentHeader.values = [
    "STT",
    "Phòng ban",
    "Lượt đăng ký",
    "Số lượng món",
    "Tổng tiền"
  ];
  styleHeader(departmentHeader);
  data.departmentRows.forEach((sourceRow, index) => {
    const row = worksheet.addRow([
      index + 1,
      sourceRow.name,
      sourceRow.registrationCount,
      sourceRow.totalQuantity,
      sourceRow.totalAmount
    ]);
    row.getCell(5).numFmt = "#,##0 [$₫-vi-VN]";
  });
  const departmentTotalRow = worksheet.addRow([
    "TỔNG",
    "",
    data.totals.registrationCount,
    data.totals.totalQuantity,
    data.totals.totalAmount
  ]);
  worksheet.mergeCells(
    departmentTotalRow.number,
    1,
    departmentTotalRow.number,
    2
  );
  departmentTotalRow.getCell(5).numFmt = "#,##0 [$₫-vi-VN]";
  styleTableRows(
    worksheet,
    6,
    departmentTotalRow.number - 1,
    departmentTotalRow,
    [2]
  );
  worksheet.autoFilter = {
    from: { row: 5, column: 1 },
    to: { row: 5, column: 5 }
  };
  worksheet.views = [{ state: "frozen", ySplit: 5 }];
  return worksheet;
}

function addFoodWorksheet(workbook, data, options) {
  const worksheet = workbook.addWorksheet("Theo món ăn");
  worksheet.columns = [
    { width: 10 },
    { width: 32 },
    { width: 20 },
    { width: 20 }
  ];
  prepareWorksheet(worksheet, data.month, 4, options);

  styleSectionTitle(worksheet, 4, "THỐNG KÊ THEO MÓN ĂN", 4);
  const foodHeader = worksheet.getRow(5);
  foodHeader.values = ["STT", "Món ăn", "Tổng số lượng", "Tổng tiền"];
  styleHeader(foodHeader);
  data.foodRows.forEach((sourceRow, index) => {
    const row = worksheet.addRow([
      index + 1,
      sourceRow.name,
      sourceRow.totalQuantity,
      sourceRow.totalAmount
    ]);
    row.getCell(4).numFmt = "#,##0 [$₫-vi-VN]";
  });
  const foodTotalRow = worksheet.addRow([
    "TỔNG",
    "",
    data.totals.totalQuantity,
    data.totals.totalAmount
  ]);
  worksheet.mergeCells(foodTotalRow.number, 1, foodTotalRow.number, 2);
  foodTotalRow.getCell(4).numFmt = "#,##0 [$₫-vi-VN]";
  styleTableRows(
    worksheet,
    6,
    foodTotalRow.number - 1,
    foodTotalRow,
    [2]
  );
  worksheet.autoFilter = {
    from: { row: 5, column: 1 },
    to: { row: 5, column: 4 }
  };
  worksheet.views = [{ state: "frozen", ySplit: 5 }];
  return worksheet;
}

export function buildLunchRegistrationWorkbook(
  registrations,
  departmentOptions,
  month,
  options = {}
) {
  const data = buildLunchExportData(registrations, departmentOptions, month);
  const workbook = new Excel.Workbook();
  workbook.creator = "PMCC";
  workbook.created = new Date();

  addOverviewWorksheet(workbook, data, options);
  addDepartmentWorksheet(workbook, data, options);
  addFoodWorksheet(workbook, data, options);
  workbook.eachSheet(worksheet => applyMinimumFontSize(worksheet));

  return { workbook, data };
}

export function buildLunchDailyExportData(registrations = [], date) {
  const normalizedDate = normalizeDate(date);
  const dailyRegistrations = registrations.filter(
    registration =>
      registration &&
      registration.date === normalizedDate &&
      (!registration.status || registration.status === "REGISTERED")
  );
  const rows = dailyRegistrations
    .map(registration => {
      const foods = (registration.items || []).map(item => ({
        name: item.foodName || "Chưa xác định",
        quantity: getQuantity(item)
      }));
      return {
        employeeId: registration.employeeId || registration.id || "",
        employeeNo: registration.employeeNo || "",
        employeeName: registration.employeeName || "Chưa xác định",
        departmentName: registration.departmentName || "Chưa xác định",
        foods,
        foodText: foods
          .map(food => `${food.name} x ${food.quantity}`)
          .join("\n"),
        note: registration.note || ""
      };
    })
    .sort((a, b) => {
      const departmentOrder = a.departmentName.localeCompare(
        b.departmentName,
        "vi"
      );
      if (departmentOrder) return departmentOrder;
      const employeeOrder = a.employeeName.localeCompare(b.employeeName, "vi");
      return employeeOrder || a.employeeId.localeCompare(b.employeeId);
    });

  const foodMap = new Map();
  dailyRegistrations.forEach(registration => {
    (registration.items || []).forEach(item => {
      const key = getItemKey(item);
      const quantity = getQuantity(item);
      const current = foodMap.get(key) || {
        key,
        name: item.foodName || "Chưa xác định",
        quantity: 0,
        totalAmount: 0
      };
      current.quantity += quantity;
      current.totalAmount += getAmount(item);
      foodMap.set(key, current);
    });
  });
  const foodRows = Array.from(foodMap.values()).sort((a, b) =>
    a.name.localeCompare(b.name, "vi")
  );
  const foodTotals = foodRows.reduce(
    (result, row) => ({
      quantity: result.quantity + row.quantity,
      totalAmount: result.totalAmount + row.totalAmount
    }),
    { quantity: 0, totalAmount: 0 }
  );

  return {
    date: normalizedDate,
    rows,
    registrationCount: rows.length,
    foodRows,
    foodTotals
  };
}

function applyDailyTableBorders(worksheet, firstRow, lastRow) {
  for (let row = firstRow; row <= lastRow; row += 1) {
    worksheet.getRow(row).eachCell({ includeEmpty: true }, cell => {
      cell.border = {
        top: { style: "thin", color: { argb: DAILY_BORDER_COLOR } },
        left: { style: "thin", color: { argb: DAILY_BORDER_COLOR } },
        bottom: { style: "thin", color: { argb: DAILY_BORDER_COLOR } },
        right: { style: "thin", color: { argb: DAILY_BORDER_COLOR } }
      };
    });
  }
}

function styleDepartmentSeparator(worksheet, rowNumber) {
  const row = worksheet.getRow(rowNumber);
  row.height = 15;
  for (let column = 1; column <= 5; column += 1) {
    const cell = row.getCell(column);
    cell.value = null;
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: TOTAL_FILL }
    };
    cell.border = {
      top: { style: "medium", color: { argb: DAILY_BORDER_COLOR } },
      bottom: { style: "medium", color: { argb: DAILY_BORDER_COLOR } },
      ...(column === 1
        ? { left: { style: "thin", color: { argb: DAILY_BORDER_COLOR } } }
        : {}),
      ...(column === 5
        ? { right: { style: "thin", color: { argb: DAILY_BORDER_COLOR } } }
        : {})
    };
  }
}

export function buildLunchDailyWorkbook(registrations, date, options = {}) {
  const data = buildLunchDailyExportData(registrations, date);
  const workbook = new Excel.Workbook();
  workbook.creator = "PMCC";
  workbook.created = new Date();

  const worksheet = workbook.addWorksheet("Theo nhân viên");
  worksheet.columns = [
    { header: "STT", key: "index", width: 8 },
    { header: "Phòng ban", key: "departmentName", width: 28 },
    { header: "Họ tên", key: "employeeName", width: 28 },
    { header: "Món đăng ký", key: "foodText", width: 38 },
    { header: "Ghi chú", key: "note", width: 30 }
  ];
  worksheet.mergeCells(1, 1, 1, 5);
  worksheet.getCell("A1").value = TITLE;
  worksheet.getCell("A1").font = { bold: true, size: 16 };
  worksheet.getCell("A1").alignment = {
    horizontal: "center",
    vertical: "middle"
  };
  worksheet.getRow(1).height = 26;
  worksheet.mergeCells(2, 1, 2, 5);
  worksheet.getCell("A2").value = `Ngày: ${String(data.date)
    .split("-")
    .reverse()
    .join("/")}`;
  worksheet.getCell("A2").font = { bold: true, size: 11 };
  worksheet.getCell("A2").alignment = {
    horizontal: "center",
    vertical: "middle"
  };
  worksheet.mergeCells(3, 1, 3, 5);
  worksheet.getCell("A3").value = `Phòng ban: ${getExportDepartmentName(
    options
  )}`;
  worksheet.getCell("A3").font = { italic: true, size: 11 };
  worksheet.getCell("A3").alignment = {
    horizontal: "center",
    vertical: "middle"
  };
  worksheet.mergeCells(4, 1, 4, 5);
  worksheet.getCell("A4").value = `Tổng số người đăng ký: ${data.registrationCount}`;
  worksheet.getCell("A4").font = { bold: true, size: 11 };
  worksheet.getCell("A4").alignment = {
    horizontal: "center",
    vertical: "middle"
  };
  worksheet.views = [{ state: "frozen", ySplit: 5 }];

  const headerRow = worksheet.getRow(5);
  headerRow.values = worksheet.columns.map(column => column.header);
  styleHeader(headerRow);

  const separatorRows = [];
  data.rows.forEach((sourceRow, index) => {
    if (
      index > 0 &&
      sourceRow.departmentName !== data.rows[index - 1].departmentName
    ) {
      const separatorRow = worksheet.addRow(["", "", "", "", ""]);
      separatorRows.push(separatorRow.number);
    }
    const row = worksheet.addRow({ index: index + 1, ...sourceRow });
    row.height = Math.max(24, sourceRow.foods.length * 18);
  });
  const lastTableRow = worksheet.lastRow ? worksheet.lastRow.number : 5;
  styleTableRows(worksheet, 6, lastTableRow, null, [2, 3, 4, 5]);
  applyDailyTableBorders(worksheet, 5, lastTableRow);
  separatorRows.forEach(rowNumber =>
    styleDepartmentSeparator(worksheet, rowNumber)
  );

  worksheet.autoFilter = {
    from: { row: 5, column: 1 },
    to: { row: 5, column: 5 }
  };
  applyMinimumFontSize(worksheet);

  const foodWorksheet = workbook.addWorksheet("Theo món ăn");
  foodWorksheet.columns = [
    { header: "STT", key: "index", width: 8 },
    { header: "Tên món ăn", key: "name", width: 38 },
    { header: "Số lượng", key: "quantity", width: 20 },
    { header: "Tổng tiền", key: "totalAmount", width: 22 }
  ];
  foodWorksheet.mergeCells(1, 1, 1, 4);
  foodWorksheet.getCell("A1").value = TITLE;
  foodWorksheet.getCell("A1").font = { bold: true, size: 16 };
  foodWorksheet.getCell("A1").alignment = {
    horizontal: "center",
    vertical: "middle"
  };
  foodWorksheet.getRow(1).height = 26;
  foodWorksheet.mergeCells(2, 1, 2, 4);
  foodWorksheet.getCell("A2").value = `Ngày: ${String(data.date)
    .split("-")
    .reverse()
    .join("/")}`;
  foodWorksheet.getCell("A2").font = { bold: true, size: 11 };
  foodWorksheet.getCell("A2").alignment = {
    horizontal: "center",
    vertical: "middle"
  };
  foodWorksheet.mergeCells(3, 1, 3, 4);
  foodWorksheet.getCell("A3").value = `Phòng ban: ${getExportDepartmentName(
    options
  )}`;
  foodWorksheet.getCell("A3").font = { italic: true, size: 11 };
  foodWorksheet.getCell("A3").alignment = {
    horizontal: "center",
    vertical: "middle"
  };
  foodWorksheet.mergeCells(4, 1, 4, 4);
  foodWorksheet.getCell("A4").value = `Tổng số lượng món: ${data.foodTotals.quantity}`;
  foodWorksheet.getCell("A4").font = { bold: true, size: 11 };
  foodWorksheet.getCell("A4").alignment = {
    horizontal: "center",
    vertical: "middle"
  };

  const foodHeaderRow = foodWorksheet.getRow(5);
  foodHeaderRow.values = foodWorksheet.columns.map(column => column.header);
  styleHeader(foodHeaderRow);
  data.foodRows.forEach((sourceRow, index) => {
    const row = foodWorksheet.addRow({ index: index + 1, ...sourceRow });
    row.getCell(4).numFmt = "#,##0 [$₫-vi-VN]";
  });
  const foodTotalRow = foodWorksheet.addRow([
    "TỔNG",
    "",
    data.foodTotals.quantity,
    data.foodTotals.totalAmount
  ]);
  foodWorksheet.mergeCells(foodTotalRow.number, 1, foodTotalRow.number, 2);
  foodTotalRow.getCell(4).numFmt = "#,##0 [$₫-vi-VN]";
  styleTableRows(
    foodWorksheet,
    6,
    foodTotalRow.number - 1,
    foodTotalRow,
    [2]
  );
  foodWorksheet.autoFilter = {
    from: { row: 5, column: 1 },
    to: { row: 5, column: 4 }
  };
  foodWorksheet.views = [{ state: "frozen", ySplit: 5 }];
  applyMinimumFontSize(foodWorksheet);

  return { workbook, data };
}

export async function exportLunchRegistrationDailyExcel(
  registrations,
  date,
  options = {}
) {
  const { workbook, data } = buildLunchDailyWorkbook(
    registrations,
    date,
    options
  );
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  });
  const [year, month, day] = data.date.split("-");
  const departmentName = getExportDepartmentName(options);
  const departmentSuffix =
    departmentName === "Tất cả phòng ban"
      ? ""
      : `_${getFileNamePart(departmentName)}`;
  const fileName = `Danh_sach_dang_ky_com${departmentSuffix}_${day}_${month}_${year}.xlsx`;
  FileSaver.saveAs(blob, fileName);
  return { fileName, data };
}

export async function exportLunchRegistrationExcel(
  registrations,
  departmentOptions,
  month,
  options = {}
) {
  const { workbook, data } = buildLunchRegistrationWorkbook(
    registrations,
    departmentOptions,
    month,
    options
  );
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  });
  const [year, monthNumber] = data.month.split("-");
  const departmentName = getExportDepartmentName(options);
  const departmentSuffix = departmentName === "Tất cả phòng ban"
    ? ""
    : `_${getFileNamePart(departmentName)}`;
  const fileName = `Danh_sach_dang_ky_com${departmentSuffix}_${monthNumber}_${year}.xlsx`;
  FileSaver.saveAs(blob, fileName);
  return { fileName, data };
}
